import { createSelector } from "reselect";

import { UnpinnedFieldAgentContext } from "@mapmycustomers/shared/types/chat/FieldAgentContext";

import { RootState } from "@app/store/rootReducer";

import composeViewContext from "./composeViewContext";
import { ComposedViewContext, ListContextEntityType, PlannerContextView } from "./ViewContextState";

export type ViewContextMode = "detached" | "live" | "pinned";

export interface ResolvedViewContext {
  entry: ComposedViewContext; // kept while detached, so the bar's analytics can still name the page
  type: ViewContextMode;
}

export interface AttachedViewContextForSend {
  context?: UnpinnedFieldAgentContext;
  type: Exclude<ViewContextMode, "detached">;
}

export interface DetachedViewContextForSend {
  context?: never; // rejects a context however the value is built, not only in a fresh literal
  type: "detached";
}

export type ViewContextForSend = AttachedViewContextForSend | DetachedViewContextForSend;

const chatViewContextState = (state: RootState) => state.chat.viewContext;

export const getListQuery = (state: RootState, entityType: ListContextEntityType) =>
  chatViewContextState(state).listQueries[entityType];

export const getMapQuery = (state: RootState) => chatViewContextState(state).mapQuery;

export const getMapPanelQuery = (state: RootState) => chatViewContextState(state).mapPanelQuery;

export const getPlannerQuery = (state: RootState, view: PlannerContextView) =>
  chatViewContextState(state).plannerQueries[view];

export const getTerritoryPinsBounds = (state: RootState) =>
  chatViewContextState(state).territoryPinsBounds;

export const getTerritoryQuery = (state: RootState) => chatViewContextState(state).territoryQuery;

export const getViewContextEntries = (state: RootState) => chatViewContextState(state).entries;

// The single home of the pin and detach rules (D1, OQ-5): a pin beats the composed page.
export const getResolvedViewContext = createSelector(
  chatViewContextState,
  ({ detachedAnchorKey, entries, pinned }): ResolvedViewContext | undefined => {
    if (pinned) {
      return { entry: pinned, type: "pinned" };
    }
    const entry = composeViewContext(entries);
    return entry
      ? { entry, type: entry.anchorKey === detachedAnchorKey ? "detached" : "live" }
      : undefined;
  }
);

export const getViewContextForSend = createSelector(
  getResolvedViewContext,
  (resolved): ViewContextForSend =>
    resolved?.type === "detached"
      ? { type: "detached" }
      : { context: resolved?.entry.context, type: resolved?.type ?? "live" }
);
